<template>
    <div>
        <div class="flex-body pb-2" v-if="loaded">
            <div class="py-2" v-if="!rows.length">
                <p class="lead text-semibold mb-hf">
                    No new employees this pay period.
                </p>
                <p>
                    If that's correct, continue.
                </p>
            </div>

            <div class="py-2" v-else>
                <p class="lead text-semibold mb-hf">
                    {{ rows.length }} new {{ rows.length == 1 ? 'employee needs' : 'employees need' }} to be added to payroll.
                </p>
                <p class="text-muted">
                    Review each one and correct anything flagged. Only hourly employees can be added here.
                </p>

                <div class="d-flex flex-wrap mb-2" role="group" aria-label="Show">
                    <button type="button" v-for="f in filters" :key="f.key" class="btn btn-sm btn-icon-outline btn-toggle mr-1 new-hire-filter" :class="{'btn-toggle-active': filter == f.key, 'new-hire-filter-warning': f.key == 'attention' && f.count && filter != f.key}" :aria-pressed="filter == f.key ? 'true' : 'false'" :disabled="f.disabled" @click="filter = f.key">
                        {{ f.label }}<span class="new-hire-filter-count">{{ f.count }}</span>
                    </button>
                </div>

                <!-- "set for all", over the matching columns of the rows below -->
                <div class="d-flex align-items-center px-2 pb-1">
                    <span class="collapse-trigger mr-1 invisible" aria-hidden="true">
                        <span class="widget-toggle-wrapper">
                            <collapse-toggle-button />
                        </span>
                    </span>
                    <div class="new-hire-name text-muted">Set for all</div>
                    <div class="new-hire-field" v-for="field in fillFields" :key="field.name" :style="{width: field.width}">
                        <select v-if="field.options" class="form-control form-control-sm" :aria-label="`Set ${field.label} for all`" :disabled="checking" @change="setForAll(field.name, $event)">
                            <option value="">Choose&hellip;</option>
                            <option v-for="option in field.options" :key="option.value" :value="option.value">{{ option.text }}</option>
                        </select>
                    </div>
                    <div class="ml-auto new-hire-status"></div>
                    <label class="mb-0 new-hire-skip">
                        <input type="checkbox" :checked="allSkipped" :disabled="checking" @change="skipAll($event.target.checked)"> Skip all
                    </label>
                </div>

                <new-hire-row
                    v-for="row in visibleRows"
                    :key="row.idx"
                    :row="row"
                    :choices="choices"
                    :save-status="saveStatus[row.idx]"
                    :expanded="!!expanded[row.idx]"
                    @toggle="expanded[row.idx] = !expanded[row.idx]"
                    @change="onRowChange(row.idx, $event)"
                    @retry="queue.retry(row.idx)"
                />

                <p class="text-muted py-2 mb-0" v-if="!visibleRows.length">
                    No employees in this view.
                </p>
            </div>

            <div class="py-2 border-top">
                <div class="row row-alt">
                    <div class="col-6">
                        <router-link class="btn btn-outline-primary" :to="{name: 'pos-integration', query: {'initial': taskDetail.id}}">Back</router-link>
                    </div>
                    <div class="col-6 text-right">
                        <p>
                            <button type="button" class="btn btn-primary" :disabled="!canContinue" @click="goNext">Continue</button>
                        </p>
                    </div>
                </div>

                <p class="mb-0 text-right" v-if="counts.attention">
                    <i aria-hidden="true" class="fas fa-exclamation-triangle text-warning mr-hf"></i>
                    <strong class="text-bold text-warning-dark">{{ counts.attention }}</strong>
                    {{ counts.attention == 1 ? 'employee needs' : 'employees need' }} attention before you can continue.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue'

import NewHireRow from './POSIntegrationNewHireRow'
import { sortForReview, filterRows, quickFields, RowSaveQueue } from './new-hires'

export default {
    props: ['client', 'taskDetail'],
    components: {NewHireRow},
    data() {
        return {
            loaded: false,
            rows: [],
            order: [],
            counts: {total: 0, ready: 0, attention: 0, skipped: 0},
            choices: {paygroup: [], employment_category: [], org_levels: []},
            filter: 'all',
            expanded: {},
            saveStatus: {},
            checking: false,
            queue: null,
        }
    },
    computed: {
        baseUrl() {
            return `/clients/${this.client.id}/pos-integration/${this.$route.params.taskId}/new-hires`
        },
        routeParams() {
            return {hashedClientId: this.$route.params.hashedClientId, taskId: this.$route.params.taskId}
        },
        filters() {
            return [
                {key: 'all', label: 'All', count: this.counts.total},
                {key: 'attention', label: 'Need attention', count: this.counts.attention},
                {key: 'ready', label: 'Ready', count: this.counts.ready},
                {key: 'skipped', label: 'Skipped', count: this.counts.skipped},
            ].map(f => ({...f, disabled: !f.count && f.key != this.filter}))
        },
        orderedRows() {
            const byIdx = {}
            this.rows.forEach(row => {
                byIdx[row.idx] = row
            })
            return this.order.map(idx => byIdx[idx]).filter(Boolean)
        },
        // pay rate has no options, so its cell stays empty
        fillFields() {
            return quickFields(this.choices)
        },
        visibleRows() {
            return filterRows(this.orderedRows, this.filter)
        },
        allSkipped() {
            return this.rows.length > 0 && this.rows.every(row => row.skip)
        },
        unsaved() {
            return Object.values(this.saveStatus).some(status => status == 'saving' || status == 'failed')
        },
        canContinue() {
            return !this.checking && !this.unsaved && this.counts.attention == 0
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Point-of-Sale Integration')
        this.queue = markRaw(new RowSaveQueue(
            (idx, change) => this.$api.patch(`${this.baseUrl}/${idx}`, change),
            (idx, status, response) => {
                this.saveStatus[idx] = status
                if (response) {
                    this.applyServer(response, false)
                }
            },
        ))
        this.load()
    },
    methods: {
        load() {
            this.$store.dispatch('START_LOADING')
            return this.$api.get(this.baseUrl).then(resp => {
                this.applyServer(resp, true)
                this.order = sortForReview(resp.rows).map(row => row.idx)
                resp.rows.forEach(row => {
                    if (row.status == 'attention') {
                        this.expanded[row.idx] = true
                    }
                })
                this.loaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        // replaceValues: take the server's field values (load, bulk change). Otherwise
        // keep what is on screen and take only errors, warnings, status and hours.
        applyServer(resp, replaceValues) {
            const current = {}
            this.rows.forEach(row => {
                current[row.idx] = row
            })
            this.rows = resp.rows.map(row => {
                const mine = current[row.idx]
                if (replaceValues || !mine) {
                    return row
                }
                const saving = this.saveStatus[row.idx] == 'saving'
                return {...row, values: mine.values, skip: saving ? mine.skip : row.skip}
            })
            this.counts = resp.counts
            this.choices = resp.choices
        },
        onRowChange(idx, change) {
            const row = this.rows.find(r => r.idx == idx)
            if ('skip' in change) {
                row.skip = change.skip
            }
            if (change.values) {
                Object.assign(row.values, change.values)
            }
            this.queue.enqueue(idx, change)
        },
        bulk(change) {
            this.checking = true
            return this.$api.post(`${this.baseUrl}/bulk`, change).then(resp => {
                this.applyServer(resp, true)
                this.checking = false
            }).catch(errors => {
                this.checking = false
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        skipAll(skip) {
            this.bulk({skip: skip})
        },
        setForAll(field, event) {
            const value = event.target.value
            event.target.value = ''
            if (value) {
                this.bulk({values: {[field]: value}})
            }
        },
        goNext() {
            // the server decides, and records that this step was completed:
            // another tab or a new Payroll record may have changed things
            this.checking = true
            return this.$api.post(`${this.baseUrl}/confirm`, {}).then(resp => {
                this.applyServer(resp, false)
                this.checking = false
                if (resp.confirmed) {
                    this.$router.push({name: 'pos-integration-review', params: this.routeParams})
                }
            }).catch(errors => {
                this.checking = false
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
    },
}
</script>

<style scoped>
    .new-hire-filter {
        background-color: #fff;
    }

    .new-hire-filter:disabled {
        opacity: 0.45;
    }

    .new-hire-filter-count {
        margin-left: 0.5em;
        font-weight: 400;
        opacity: 0.7;
    }

    .new-hire-filter-warning {
        border-color: #DEBB00;
        background-color: #FBF4CC;
    }
</style>
